import { useState, useRef, useEffect } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Card,
  CardContent,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
} from "@mui/material";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useDispatch } from "react-redux";
import { sendSmsOtp } from "../../State/Auth/Action";
import api from "../../services/api";
import toast from "react-hot-toast";
import { jwtDecode } from "jwt-decode";
import { useMyContext } from "../../store/ContextApi";
import { FcGoogle } from "react-icons/fc";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const apiUrl = process.env.REACT_APP_API_URL;

const Login = () => {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [open, setOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [jwtToken, setJwtToken] = useState("");
  const { setToken, token } = useMyContext();
  const [showCustomerName, setShowCustomerName] = useState(false);
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("customer");

  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const [timeLeft, setTimeLeft] = useState(120);
  const [timerExpired, setTimerExpired] = useState(false);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (/^\d{0,10}$/.test(value)) {
      setPhone(value);
    }
  };

  const showAlert = (message) => {
    setAlertMessage(message);
    setAlertOpen(true);
  };

  const handleCheckboxChange = (event) => {
    setRole(event.target.checked ? "dealer" : "customer");
  };

  const handleGetOtp = () => {
    if (phone.length !== 10) {
      showAlert("Please enter a valid 10-digit phone number");
      return;
    }
    dispatch(sendSmsOtp(phone));
    setOpen(true);
  };

  const handleResendOtp = () => {
    dispatch(sendSmsOtp(phone));
    setTimeLeft(120);
    setTimerExpired(false);
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 3) {
      otpRefs[index + 1]?.current?.focus();
    }
  };

  const handleVerifyOtp = () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 4) {
      showAlert("Please enter a valid 4-digit OTP");
      return;
    }
    const otpLoginRequest = { phoneNumber: phone, otp: otpValue };
    otpSignIn(otpLoginRequest);
  };

  const otpSignIn = async (input) => {
    try {
      const response = await api.post("/auth/public/otp-signin", input);
      toast.success("Login Successful");
      if (response.status === 200 && response.data.jwtToken) {
        setJwtToken(response.data.jwtToken);
        const decodedToken = jwtDecode(response.data.jwtToken);
        handleSuccessfulLogin(response.data.jwtToken, decodedToken);
      } else {
        toast.error(
          "Login failed. Please check your credentials and try again."
        );
      }
    } catch (error) {
      if (error?.response?.data?.userExists === false) {
        setShowCustomerName(true);
      } else {
        toast.error("Invalid credentials");
      }
    }
  };

  const handleSave = async () => {
    const otpValue = otp.join("");
    const input = { phoneNumber: phone, otp: otpValue, fullName, role };
    const response = await api.post("/auth/public/signup-by-phone", input);

    if (response.status === 200 && response.data.jwtToken) {
      setJwtToken(response.data.jwtToken);
      const decodedToken = jwtDecode(response.data.jwtToken);
      handleSuccessfulLogin(response.data.jwtToken, decodedToken);
    } else {
      toast.error(
        "Unable to register. Please check your credentials and try again."
      );
    }
  };

  const handleSuccessfulLogin = (token, decodedToken) => {
    const user = {
      username: decodedToken.sub,
      roles: decodedToken.roles ? decodedToken.roles.split(",") : [],
    };
    localStorage.setItem("JWT_TOKEN", token);
    localStorage.setItem("USER", JSON.stringify(user));
    setToken(token);
    navigate("/home");
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs[index - 1]?.current?.focus();
    }
  };

  useEffect(() => {
    let timer;
    if (open) {
      setTimeLeft(120);
      setTimerExpired(false);
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setTimerExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [open]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen gradient-bg flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <Card className="card shadow-strong border-0 overflow-hidden">
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-6 text-center">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <LockOpenIcon className="text-white text-2xl" />
            </motion.div>
            <Typography variant="h4" className="text-white font-bold mb-2">
              Welcome Back
            </Typography>
            <Typography variant="body2" className="text-white/80">
              Sign in to your account to continue
            </Typography>
          </div>
          
          <CardContent className="p-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="space-y-6"
            >
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <PhoneAndroidIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="Enter your phone number"
                    maxLength={10}
                    className="input-field pl-12"
                  />
                </div>
              </div>

              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleGetOtp}
                  className="btn-primary py-3 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                  startIcon={<LockOpenIcon />}
                >
                  Get OTP
                </Button>
              </motion.div>

              <div className="text-center">
                <Typography variant="body2" className="text-gray-500">
                  By continuing, you agree to our{" "}
                  <a href="/terms" className="text-primary-600 hover:text-primary-700 font-medium">
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a href="/privacy" className="text-primary-600 hover:text-primary-700 font-medium">
                    Privacy Policy
                  </a>
                </Typography>
              </div>
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>

      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: "rounded-2xl shadow-strong border-0"
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-6 text-center">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, duration: 0.3 }}
              className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3"
            >
              <AccessTimeIcon className="text-white text-xl" />
            </motion.div>
            <Typography variant="h5" className="text-white font-bold">
              Verify OTP
            </Typography>
            <Typography variant="body2" className="text-white/80 mt-1">
              Enter the code sent to your phone
            </Typography>
          </div>
          
          <DialogContent className="p-8">
            <div className="text-center space-y-6">
              <div>
                <Typography className="text-gray-700 mb-2">
                  We sent a verification code to
                </Typography>
                <Typography className="font-semibold text-primary-600 text-lg">
                  +91 {phone}
                </Typography>
              </div>

              <div className="flex justify-center">
                <div className={`px-4 py-2 rounded-lg ${
                  timerExpired 
                    ? "bg-red-100 text-red-700" 
                    : "bg-primary-100 text-primary-700"
                }`}>
                  <Typography className="font-semibold">
                    {timerExpired
                      ? "Time expired! Request new OTP"
                      : `Time left: ${formatTime(timeLeft)}`}
                  </Typography>
                </div>
              </div>

              <motion.div
                className="flex gap-3 justify-center"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="tel"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    ref={otpRefs[index]}
                    maxLength={1}
                    className="w-14 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200"
                  />
                ))}
              </motion.div>

              {timerExpired && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    onClick={handleResendOtp}
                    className="btn-secondary"
                  >
                    Resend OTP
                  </Button>
                </motion.div>
              )}
            </div>
          </DialogContent>
          
          <DialogActions className="p-6 pt-0 gap-3">
            <Button 
              onClick={() => setOpen(false)}
              className="btn-secondary"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={handleVerifyOtp}
              disabled={timerExpired}
              className="btn-primary"
            >
              Verify OTP
            </Button>
          </DialogActions>
        </motion.div>
      </Dialog>

      <Dialog
        open={showCustomerName}
        onClose={() => setShowCustomerName(false)}
      >
        <DialogTitle className="text-center font-bold">
          Customer Name
        </DialogTitle>
        <DialogContent className="flex flex-col items-center">
          <TextField
            variant="outlined"
            label="Enter Full Name"
            onChange={(e) => setFullName(e.target.value)}
            inputProps={{
              maxLength: 30,
              style: { textAlign: "center", fontSize: "20px" },
            }}
            className="w-full h-12 text-xl text-center border-2 border-gray-300 rounded-md focus:border-blue-500"
          />
          <Box className="pt-4 flex items-center">
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={handleCheckboxChange}
                    sx={{
                      color: "primary.main",
                      "&.Mui-checked": { color: "primary.main" },
                      transform: "scale(1.3)",
                    }}
                  />
                }
                label={
                  <span
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: 400,
                      color: "#1976d2",
                    }}
                  >
                    Are you a Bike Dealer?
                  </span>
                }
              />
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCustomerName(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={alertOpen} onClose={() => setAlertOpen(false)}>
        <DialogTitle>Alert</DialogTitle>
        <DialogContent>
          <Typography>{alertMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertOpen(false)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Login;
